// src/components/ReferralDashboard.js
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { standardUserApi } from "../services/standardUserApi";
import memberstackDOM from "@memberstack/dom";
import { getMemberstackKey } from "../utils";
import useNoIndex from "../hooks/useNoIndex";
import mixpanel from "mixpanel-browser";
import "../css/ReferralDashboard.css";

const currentKey = getMemberstackKey();
const memberstack = memberstackDOM.init({
  publicKey: currentKey,
});

const ReferralDashboard = () => {
  useNoIndex();

  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);
  const [shareMethod, setShareMethod] = useState(null);
  const navigate = useNavigate();

  // Fetch referral configuration
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch("/api/referral/config");
        const data = await response.json();
        setConfig(data);
      } catch (err) {
        console.error("Error fetching referral config:", err);
      }
    };
    fetchConfig();
  }, []);

  // Fetch referral statistics
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = localStorage.getItem("memberstackToken");
        if (!token) {
          setError("Please log in to view referrals");
          setLoading(false);
          return;
        }

        const response = await fetch("/api/referral/stats", {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        });

        if (!response.ok) {
          throw new Error("Failed to fetch referral stats");
        }

        const data = await response.json();
        setStats(data);

        // Track dashboard view
        if (mixpanel && mixpanel.__loaded) {
          mixpanel.track("Referral Dashboard Viewed", {
            total_credits_earned: data.lifetime_credits_earned,
            active_referrals: data.active_referrals_count,
            successful_referrals: data.successful_conversions_count,
          });
        }
      } catch (err) {
        console.error("Error fetching referral stats:", err);
        setError("Failed to load referral statistics");
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  // Copy referral link to clipboard
  const handleCopyLink = () => {
    if (!stats?.referralUrl) return;

    navigator.clipboard.writeText(stats.referralUrl).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);

      if (mixpanel && mixpanel.__loaded) {
        mixpanel.track("Referral Link Copied", {
          method: "copy_button",
        });
      }
    });
  };

  // Share via different methods
  const handleShare = (method) => {
    if (!stats?.referralUrl) return;

    const shareText = `Join me on OkGather! Get a 30-day free trial and then £10 off your second month with my referral link: ${stats.referralUrl}`;
    const encodedText = encodeURIComponent(shareText);
    const encodedUrl = encodeURIComponent(stats.referralUrl);

    let shareUrl = "";

    switch (method) {
      case "whatsapp":
        shareUrl = `https://wa.me/?text=${encodedText}`;
        break;
      case "email":
        shareUrl = `mailto:?subject=${encodeURIComponent(
          "Join me on OkGather!"
        )}&body=${encodedText}`;
        break;
      case "sms":
        shareUrl = `sms:?&body=${encodedText}`;
        break;
      case "twitter":
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
          "Join me on OkGather and get a 30-day free trial!"
        )}&url=${encodedUrl}`;
        break;
      case "facebook":
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        break;
      default:
        return;
    }

    window.open(shareUrl, "_blank");

    if (mixpanel && mixpanel.__loaded) {
      mixpanel.track("Referral Link Shared", {
        method: method,
      });
    }
  };

  // Generate badge icon
  const getBadgeIcon = (achievement) => {
    return achievement.icon || "🏆";
  };

  // Calculate progress to next milestone
  const getNextMilestone = () => {
    if (!stats) return null;

    const milestones = [
      { count: 1, name: "Social Butterfly", icon: "🦋" },
      { count: 3, name: "Connector", icon: "🌟" },
      { count: 5, name: "Super Advocate", icon: "🏆" },
      { count: 10, name: "Legend", icon: "👑" },
    ];

    const current = stats.successful_conversions_count || 0;
    const next = milestones.find((m) => m.count > current);

    if (!next) return null;

    return {
      ...next,
      remaining: next.count - current,
      progress: (current / next.count) * 100,
    };
  };

  // Calculate savings percentage
  const getSavingsPercentage = () => {
    if (!stats) return 0;
    const monthlyPrice = 6.99;
    const creditsThisMonth = parseFloat(stats.credits_applied_this_month || 0);
    return Math.min(Math.round((creditsThisMonth / monthlyPrice) * 100), 100);
  };

  // Friend status badge component
  const FriendStatusBadge = ({ status }) => {
    const statusConfig = {
      active: { label: "Active", color: "green", icon: "✓" },
      pending: { label: "Pending", color: "yellow", icon: "⏱️" },
      expired: { label: "Expired", color: "gray", icon: "⏰" },
      cancelled: { label: "Cancelled", color: "red", icon: "✗" },
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <span className={`status-badge status-${config.color}`}>
        {config.icon} {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="referral-dashboard-container">
        <div className="loading-spinner">Loading your referral dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="referral-dashboard-container">
        <div className="error-message">{error}</div>
        <button onClick={() => navigate("/edit-profile")} className="back-btn">
          Back to My Profile
        </button>
      </div>
    );
  }

  const nextMilestone = getNextMilestone();
  const savingsPercentage = getSavingsPercentage();

  return (
    <div className="referral-dashboard-container">
      <div className="referral-dashboard">
        {/* Header */}
        <div className="dashboard-header">
          <h1>🎉 Share & Save</h1>
          <button onClick={() => navigate("/edit-profile")} className="close-btn">
            ✕
          </button>
        </div>

        {/* Credit Summary Cards */}
        <div className="credit-summary">
          <div className="credit-card">
            <div className="credit-icon">💰</div>
            <div className="credit-label">Lifetime Earned</div>
            <div className="credit-amount">
              £{parseFloat(stats.lifetime_credits_earned || 0).toFixed(2)}
            </div>
          </div>

          <div className="credit-card">
            <div className="credit-icon">🏦</div>
            <div className="credit-label">Credits Remaining</div>
            <div className="credit-amount">
              £{parseFloat(stats.total_credits_banked || 0).toFixed(2)}
            </div>
          </div>

          <div className="credit-card">
            <div className="credit-icon">✅</div>
            <div className="credit-label">Friends Joined</div>
            <div className="credit-amount">{stats.successful_conversions_count || 0}</div>
          </div>

          <div className="credit-card">
            <div className="credit-icon">🔥</div>
            <div className="credit-label">Currently Active</div>
            <div className="credit-amount">{stats.active_referrals_count || 0}</div>
          </div>
        </div>

        {/* Savings Progress Bar */}
        {stats.credits_applied_this_month > 0 && (
          <div className="savings-banner">
            <div className="savings-header">
              <span className="savings-icon">💎</span>
              <span className="savings-text">
                You're saving {savingsPercentage}% this month!
              </span>
            </div>
            <div className="progress-bar-container">
              <div
                className="progress-bar-fill"
                style={{ width: `${savingsPercentage}%` }}
              ></div>
            </div>
            <div className="savings-detail">
              £{parseFloat(stats.credits_applied_this_month).toFixed(2)} credit applied
              {savingsPercentage === 100 && " - Your membership is FREE! 🎊"}
            </div>
          </div>
        )}

        {/* Share Referral Link */}
        <div className="share-section">
          <h2>Your Referral Link</h2>
          <div className="referral-link-container">
            <input
              type="text"
              value={stats.referralUrl || ""}
              readOnly
              className="referral-link-input"
            />
            <button
              onClick={handleCopyLink}
              className={`copy-btn ${copySuccess ? "copied" : ""}`}
            >
              {copySuccess ? "✓ Copied!" : "📋 Copy"}
            </button>
          </div>

          <div className="share-methods">
            <button
              onClick={() => handleShare("whatsapp")}
              className="share-btn whatsapp"
            >
              WhatsApp
            </button>
            <button onClick={() => handleShare("email")} className="share-btn email">
              Email
            </button>
            <button onClick={() => handleShare("sms")} className="share-btn sms">
              SMS
            </button>
            <button onClick={() => handleShare("twitter")} className="share-btn twitter">
              Twitter
            </button>
            <button onClick={() => handleShare("facebook")} className="share-btn facebook">
              Facebook
            </button>
          </div>
        </div>

        {/* Why Share Section */}
        <div className="why-share-section">
          <h2>Why your friends will thank you</h2>
          <p className="why-share-intro">
            You're not just sharing a discount - you're introducing them to a community where they can:
          </p>
          <div className="benefits-grid">
            <div className="benefit-item">
              <div className="benefit-icon">🎯</div>
              <div className="benefit-text">Connect with people who share their niche interests</div>
            </div>
            <div className="benefit-item">
              <div className="benefit-icon">💼</div>
              <div className="benefit-text">Build meaningful career connections and network</div>
            </div>
            <div className="benefit-item">
              <div className="benefit-icon">💕</div>
              <div className="benefit-text">Meet potential partners through shared passions</div>
            </div>
            <div className="benefit-item">
              <div className="benefit-icon">🌟</div>
              <div className="benefit-text">Discover great gatherings and meet new people</div>
            </div>
          </div>
          <p className="why-share-footer">
            After attending a few gatherings, they'll probably be super thankful you introduced them to OkGather. The value they get depends entirely on the gathering themes they choose to explore!
          </p>
        </div>

        {/* Achievements Section */}
        {stats.achievements && stats.achievements.length > 0 && (
          <div className="achievements-section">
            <h2>🏆 Achievements</h2>
            <div className="achievements-grid">
              {stats.achievements.map((achievement) => (
                <div
                  key={achievement.type}
                  className={`achievement-badge ${
                    achievement.is_new ? "new-badge" : ""
                  }`}
                >
                  <div className="achievement-icon">{achievement.icon}</div>
                  <div className="achievement-name">{achievement.name}</div>
                  {achievement.is_new && <div className="new-indicator">NEW!</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Next Milestone Preview */}
        {nextMilestone && (
          <div className="next-milestone">
            <div className="milestone-header">
              <span className="milestone-icon">{nextMilestone.icon}</span>
              <span className="milestone-text">Next Milestone</span>
            </div>
            <div className="milestone-progress">
              <div className="progress-bar-container">
                <div
                  className="progress-bar-fill"
                  style={{ width: `${nextMilestone.progress}%` }}
                ></div>
              </div>
              <div className="milestone-detail">
                Invite {nextMilestone.remaining} more friend
                {nextMilestone.remaining > 1 ? "s" : ""} to unlock{" "}
                <strong>{nextMilestone.name}</strong>!
              </div>
            </div>
          </div>
        )}

        {/* Friends List */}
        <div className="friends-section">
          <h2>👥 Your Friends ({stats.referrals?.length || 0})</h2>

          {stats.referrals && stats.referrals.length > 0 ? (
            <div className="friends-list">
              {stats.referrals.map((referral) => (
                <div key={referral.id} className="friend-card">
                  <div className="friend-header">
                    <div className="friend-name">
                      {referral.referred_friend_name || "Friend"}
                    </div>
                    <FriendStatusBadge status={referral.status} />
                  </div>

                  <div className="friend-details">
                    {referral.status === "active" && (
                      <>
                        <div className="friend-stat">
                          <span className="stat-label">Credit earned:</span>
                          <span className="stat-value">
                            £{parseFloat(referral.total_credits_earned).toFixed(2)} (Month{" "}
                            {referral.months_active} of 3)
                          </span>
                        </div>
                        <div className="friend-stat">
                          <span className="stat-label">Time Remaining:</span>
                          <span className="stat-value">
                            {referral.days_remaining} days
                          </span>
                        </div>
                        <div className="friend-progress">
                          <div className="progress-bar-container">
                            <div
                              className="progress-bar-fill"
                              style={{
                                width: `${(referral.months_active / 3) * 100}%`,
                              }}
                            ></div>
                          </div>
                        </div>
                      </>
                    )}

                    {referral.status === "pending" && (
                      <div className="friend-pending-msg">
                        Signed up, waiting for first payment...
                      </div>
                    )}

                    {referral.status === "expired" && (
                      <div className="friend-expired-msg">
                        {config?.durationMonths || 3}-month earning period completed!
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-friends">
              <p>You haven't referred anyone yet!</p>
              <p className="no-friends-sub">
                Share your link above to start earning credits
              </p>
            </div>
          )}
        </div>

        {/* Call to Action */}
        <div className="cta-section">
          <h3>How It Works</h3>
          <div className="how-it-works">
            <div className="step">
              <div className="step-number">1</div>
              <div className="step-text">Share your unique link with friends</div>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <div className="step-text">They get a 30-day free trial and then £10 off their second month</div>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <div className="step-text">
                You earn £{config?.referrerCreditPerMonth || 5}/month credit for {config?.durationMonths || 3} months per friend! (Limited time offer)
              </div>
            </div>
          </div>

          <div style={{
            marginTop: "20px",
            textAlign: "center",
            fontSize: "13px",
            color: "#666"
          }}>
            <a href="/referral-terms" style={{ color: "#8B5FBF", textDecoration: "underline" }}>
              View Full Terms & Conditions
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralDashboard;
